// TODO: when the time comes -- decouple this into each separate verification.
// (when global pr is about to go to prod)
// By now -- this is unreadable and unmanageable and requires shit like this:
//   {
//     verification: VerificationReport<
//       VerifiedIncomeReport &
//         VerifiedPayrollIncome &
//         VerifiedBankIncome & {
//           past_three_months_avg_total?: number;
//         }
//     >;
//   } & {
//     applicant: string;
//   }
import { Verification } from "shared/types/verification";
import { Asset } from "views/applications-id-report/slices/IdentityVerificationReportSlice/types";
import { PayStubI } from "views/applications-id-report/utils/PayStub";

export type TimePeriods = "Monthly" | "Annual" | "Every two weeks" | "Weekly";

export type Money = {
  cents: number;
  currency: string;
};

export type CodeAndDescription = {
  code?: string | null;
  description?: string | null;
};

type EvictionReportResult = {
  address: string;
  city: string;
  zip: string;
  state: string;
  court: string;
  case_number: string;
  plaintiff: string;
  notice_type: string;
  filling_date: string;
  judgement_date: string;
  judgement_amount?: Money | null;
  judgement: string;
};

type CriteriaEvictionReport = {
  housing_court_records: number;
};

export type EvictionReport = CICReport<
  EvictionReportResult,
  CriteriaEvictionReport
>;

export type ReportAddress = {
  housing_type: string;
  other?: {
    current_residence: boolean;
    housing_status: string;
    move_in_date: string;
    reason_for_leaving: string;
    start_date?: string;
  };
  rent?: {
    address: string;
    lease_document?: Asset[];
    current_residence: boolean;
    housing_status: string;
    move_in_date: string;
    reason_for_leaving: string;
    start_date: string;
    landlord_name: string;
    landlord_email: string;
    landlord_phone: string;
  };
};

export type HousingHistoryReportResult = {
  address: ReportAddress[];
  average_monthly_payment_amount: number;
  total_months_of_residence: number;
  total_number_of_residences: number;
  completed_surveys_count?: number;
  total_surveys_count?: number;
  created_at?: string;
  notes: Note[];
};

export type CriminalReportRecord = {
  criminal: number;
  criminalTraffic: number;
  nationalSexOffender: number;
  potentialOFACMatch: number;
};

export type CriminalReportOffense = {
  court?: string | null;
  status?: string | null;
  filing_date?: string | null;
  case_type?: string | null;
  description?: string | null;
  disposition?: string | null;
  disposition_date?: string | null;
  offense_code?: string | null;
  offense_date?: string | null;
  charge_file_date?: string | null;
  statute?: string | null;
  commitment_location?: string | null;
  commitment_state?: string | null;
  commitment_date?: string | null;
  release_date?: string | null;
  conviction_date?: string | null;
  conviction_location?: string | null;
  sentence_length?: string | null;
  sub_categorization?: {
    category: string;
    type: string;
  };
  is_convicted?: boolean | null;
  categorization:
    | "felony"
    | "misdemeanor"
    | "other_criminal"
    | "sex_offender"
    | "ofac";
};

export type CriminalReportResult = {
  subject: {
    full_name: string;
    first_name: string;
    middle_name?: string;
    last_name: string;
    image_url?: string | null;
    dob?: string | null;
    sex?: string | null;
    hair_color?: string | null;
    eye_color?: string | null;
    height?: string | null;
    weight?: string | null;
    ethnicity?: string | null;
    race?: string | null;
    age?: string | null;
    military_service?: string | null;
    scar_marks?: string | null;
    alias?: string | null;
    source?: string | null;
    address?: string | null;
    primary_address?: {
      street_name: string;
      street_number: string;
      city: string;
      state: string;
      zip: string;
    };
    category?: string | null;
    disclaimers?: string | null;
  };
  case_details: {
    case_number?: string | null;
    case_docket_number?: string | null;
    category?: string | null;
    case_file_date?: string | null;
    case_type: string;
    state?: string | null;
    jurisdiction?: string | null;
    court_name?: string | null;
    court_type?: string | null;
    comments?: string | null;
  };
  offenses: CriminalReportOffense[];
};

type CriteriaCriminalReport = {
  sex_offender_check: number;
};

export type CriminalReport = CICReport<
  CriminalReportResult,
  CriteriaCriminalReport
>;

export type CriminalOffenses = {
  all: number;
  convicted: number;
  drugs?: {
    any: number;
    distribution: number;
    possession: number;
  };
  conspiracyOrganizedCrime?: {
    any: number;
    conspiracyOrganizedCrime: number;
    loanSharking: number;
  };
  violence?: {
    any: number;
    violence: number;
    assault: number;
    murder: number;
    manslaughter: number;
    battery: number;
    wounding: number;
    intimidation: number;
    kidnappingImprisonment: number;
  };
  sexOffense?: {
    any: number;
    rape: number;
    sexualActsWithMinors: number;
    molestation: number;
    indecentExposure: number;
    indecency: number;
    exploitation: number;
    incest: number;
    sexualAbuse: number;
    prostitutionPandering: number;
    pornography: number;
    uncategorized: number;
  };
  theft?: {
    any: number;
    theft: number;
    burglary: number;
    extortion: number;
    fraudForgery: number;
    robbery: number;
    stolenProperty: number;
  };
  destruction?: {
    any: number;
    arson: number;
    vandalism: number;
  };
  harassment?: {
    any: number;
    harassment: number;
    stalking: number;
  };
  societal?: {
    any: number;
    obscenity: number;
    gambling: number;
    alcohol: number;
    underTheInfluence: number;
    weaponsPossession: number;
    family: number;
  };
  corporate?: {
    any: number;
    corporate: number;
  };
  other?: {
    any?: number;
    other: number;
    license: number;
    taxes: number;
    justice: number;
    fugitive: number;
    disorder: number;
    trespassing: number;
    vehicle: number;
    dumpingLittering: number;
    uncategorized: number;
  };
};

export type CriminalReportSummary = {
  total_criminal_records?: number | null;
  total_traffic_records?: number | null;
  total_felony_offenses?: CriminalOffenses;
  total_misdemeanor_offenses?: CriminalOffenses;
  total_other_criminal_offenses?: CriminalOffenses;
  total_ofac_offenses?: number | null;
  total_sex_offender_offenses?: CriminalOffenses;
};
type CICReport<T, C> = {
  criteria?: C;
  message: string;
  consumer_statement?: string | null;
  disclaimer?: string | null;
  completed_at?: string;
  finished_at?: string;
  requested_at?: string;
  // TODO: Tin will add
  // Empty if automatic.
  requested_by?: {
    email: string;
    id: number;
    // Full Name of the person.
    name: string;
  };
  manual_requested?: boolean;
  results: T[];
  // For criminal report

  error_code?: string;
  error_message?: string;
  require_approval?: boolean;

  status?: string;

  // TODO: type is called CIC but in reality this data should exist only on Cleara vendor report
  is_county_report: boolean;

  // For eviction report
  notice?: string;
  summary?: CriminalReportSummary;
  combined_summary?: CriminalReportSummary;
  grouped_results?: {
    felony_records?: T[];
    misdemeanor_records?: T[];
    other_criminal_records?: T[];
    ofac_records?: T[];
    sex_offender_records?: T[];
  };
};

interface ContactField {
  data: string;
  primary: boolean;
  type?: string;
}

interface ContactFieldAddress {
  data: {
    street: string;
    region: string;
    city: string;
    country: string;
    postal_code: string;
  };
  primary: boolean;
}

interface BankIncomeSourceOwner {
  names: string[];
  emails: ContactField[];
  addresses: ContactFieldAddress[];
  phone_numbers: ContactField[];
}

interface BankTransaction {
  date: string;
  name: string;
  amount: Money;
  check_number: any;
  transaction_id: string;
  iso_currency_code: string;
  original_description: string;
  unofficial_currency_code: any;
}

export interface BankHistoricalSummary {
  end_date: string;
  start_date: string;
  total_amount?: Money;
  transactions: BankTransaction[];
  total_amounts: {
    amount: number;
  }[];
  iso_currency_code: string;
  unofficial_currency_code: any;
}

export interface BankIncomeSource {
  end_date: string;
  account_id: string;
  start_date: string;
  total_amount: Money;
  pay_frequency: string;
  income_category: string;
  income_source_id: string;
  transaction_count: number;
  historical_summary: BankHistoricalSummary[];
  income_description: string;
  mask: string;
  name: string;
  type: string;
  owners: BankIncomeSourceOwner[];
  subtype: string;
  official_name: string;
  past_three_months_total: number;
  past_three_months_avg: Money;
  gross_monthly_income: Money;
  net_monthly_income: Money;
  income_source_type?: string;
  institutionMetaData?: InstitutionMetadata;
  forecasted_net_monthly_income?: Money;
  next_payment_date?: string;
  employer?: {
    name: string;
  };
}

export interface InstitutionMetadata {
  url: string;
  logo: string;
  name: string;
  primary_color: string;
  institution_id: string;
}

interface BankIncomeAccountOwner {
  names: string[];
  emails: ContactField[];
  addresses: ContactField[];
  phone_numbers: ContactField[];
}

interface BankIncomeAccount {
  mask: string;
  name: string;
  type: string;
  owners: BankIncomeAccountOwner[];
  subtype: string;
  account_id: string;
  official_name: string;
}

export interface BankIncomeItem {
  item_id: string;
  institution_id: string;
  institution_name: string;
  last_updated_time: string;
  bank_income_sources: BankIncomeSource[];
  bank_income_accounts: BankIncomeAccount[];
  institution_metadata: InstitutionMetadata;
  gross_monthly_income: Money;
  net_monthly_income: Money;
}

export interface BankIncome {
  items: BankIncomeItem[];
  bank_income_id: string;
  days_requested: number;
  historical_summary: BankHistoricalSummary[];
}

interface PayrollAccount {
  account_id: string;
  rate_of_pay: {
    pay_rate: string;
    pay_amount: Money;
  };
  pay_frequency: string;
}

interface DocumentMetadata {
  name: string;
  status: string;
  page_count: number;
  download_url?: string;
  document_type: string;
  asset?: {
    url: string;
    content_type: string;
    filename: string;
  };
}

interface PayPeriodDetails {
  start_date: string;
  end_date: string;
  pay_date: string;
  pay_amount: Money;
  pay_frequency: string;
}

export interface DocumentRiskSignal {
  document_reference: {
    document_id: string;
    document_name: string;
  };
  risk_summary: {
    risk_score: number;
  };
  risk_signals: {
    actual_value: string;
    expected_value: string;
    field: string | null;
    signal_description: string | null;
    has_fraud_risk: boolean;
    type: string;
    page_number: number;
  }[];
}

interface VerificationRiskSignal {
  account_id?: string;
  multi_document_risk_signals: DocumentRiskSignal[];
  single_document_risk_signals: DocumentRiskSignal[];
}

interface RiskSignal {
  item_id: string;
  verification_risk_signals: VerificationRiskSignal[];
}

export interface PayStub {
  document_metadata: DocumentMetadata;
  earnings: {
    base: Money;
    bonus: Money;
  };
  gross_earnings: Money;
  deductions: Money;
  net_pay: Money;
  pay_period_details: PayPeriodDetails;
  employer: {
    name: string;
    address?: PayStubAddress;
  };
  risk_signal?: VerificationRiskSignal;
}

interface PayrollIncomeAccount {
  account_id: string;
  mask: string;
  name: string;
  official_name: string;
}

interface PayStubAddress {
  street: string;
  region: string;
  postal_code: string;
  country: string;
  city: string;
}

export interface PayrollEmployment {
  account_id: string;
  status: string;
  start_date: string;
  end_date?: string | null;
  employer: {
    name: string;
    address?: PayStubAddress;
  };
  title: string;
  employee_type: string;
  address?: PayStubAddress;
  account?: PayrollAccount;
}

export interface PayrollIncome {
  pay_stubs: PayStub[];
  account_id: string;
  account: PayrollIncomeAccount;
  start_date: string;
  end_date: string;
  total_net: Money;
  past_three_months_avg: Money;
}

export interface PayrollItem {
  item_id: string;
  start_date: string;
  end_date: string;
  accounts: PayrollAccount[];
  updated_at: string;
  institution_id: string;
  payroll_income: PayrollIncome[];
  institution_name: string;
  institution_metadata?: InstitutionMetadata | null;
  total_net: Money;
  income_source_type?: string;
  gross_monthly_income: Money;
  net_monthly_income: Money;
}

export type VerifiedBankIncome = VerifiedIncome & {
  start_date: string;
  end_date: string;
  bank_income: BankIncome[];
};

export type VerifiedPayrollIncome = VerifiedIncome & {
  start_date: string;
  end_date: string;
  items: PayrollItem[];
  risk_signal?: RiskSignal[];
  employment?: PayrollEmployment[] | null;
};

export enum VerifiedIncomeFlowTypes {
  PAYROLL_DIGITAL_INCOME = "payroll_digital_income",
  PAYROLL_DOCUMENT_INCOME = "payroll_document_income"
}

type VerifiedIncome = {
  income_source_type: "bank" | "payroll" | "document";
  flow_types?: VerifiedIncomeFlowTypes[] | null;
  monthly_rent_amount: Money;
  income_count: number;
  past_three_months_avg: Money;
  income_to_rent_ratio: number;
  criteria?: {
    net_income: Money;
    net_income_to_rent_ratio: number;
  };
};

type PreTaxIncome = {
  income_amount: number;
  income_type: string;
};

export enum VerificationMethodType {
  BankStatement = "Bank Statements",
  PayStub = "Pay Stub",
  Other = "Other"
}

interface SelfEmployed {
  company_name: string;
  current_income: boolean;
  job_title: string;
  end_date: string;
  start_date: string;
  pre_tax_income: PreTaxIncome;
  //old flow types
  offer_letter_file: {
    asset?: Asset["asset"];
  };
  verification_method: {
    verification_type: VerificationMethodType;
    other_files: Asset["asset"][];
    bank_statement_files: Asset["asset"][];
    pay_stub_files: Asset["asset"][];
  };
}

interface Employment {
  company_name: string;
  current_income: boolean;
  job_title: string;
  pre_tax_income: PreTaxIncome;
  start_date: string;
  end_date?: string;
  supervisor: {
    first_name: string;
    last_name: string;
    supervisor_contact_type: string;
    supervisor_phone: string;
    //I'm not sure if this is correct
    supervisor_email: string;
    //
    offer_letter_file?: Asset;
    verification_method?: {
      verification_type: string;
      pay_stub_files?: (Asset | null)[];
      other_files?: (Asset | null)[];
      bank_statement_files?: (Asset | null)[];
    };
  };
}

export type SelfReportedVerificationItem = {
  monthly_income: Money;
  income_source: SelfReportedIncomeSource;
  employment?: Employment;
  self_employed?: SelfEmployed;
  offer_letter?: {
    company_name: string;
    start_date: string;
    job_title: string;
    offer_letter_file?: {
      asset?: Asset["asset"];
    };
    supervisor?: {
      first_name: string;
      last_name: string;
      supervisor_contact_type: string;
      supervisor_phone?: string;
      supervisor_email?: string;
    };
  };
  other_income?: {
    current_income: boolean;
    income_description: string;
    paid_by: string;
    start_date: string;
    pre_tax_income: PreTaxIncome;
    end_date?: string;
  };
};

export type SelfAttestedIncome = {
  do_you_have_source_of_income: "Yes" | "No";
  financial_situation?: "Retiree" | "Student" | "Home-maker";
  criteria?: {
    gross_income: Money;
    gross_income_to_rent_ratio: number;
  };
  income_sources: SelfReportedVerificationItem[];
  income_to_rent_ratio?: number;
  current_income?: Money;
};

type Deductions = {
  canonical_description: string;
  current_pay: Money;
  description: string;
  ytd_pay: Money;
};

export type VerifiedIncomeSource = {
  employer: string;
  end_date: string;
  start_date: string;
  pay_stubs: PayStubI[];
  net_monthly_income: Money;
  gross_monthly_income: Money;
};

export interface NetIncomeSummarySource {
  pay_date: string;
  description: string;
  net_pay?: Money;
}

export interface NetIncomeSummary {
  month: string;
  sources: NetIncomeSummarySource[];
  total_net?: Money;
}

export interface Note {
  attachment?: {
    changes: {
      field: string;
      from: string;
      to: string;
    }[];
    changed_by?: {
      id: number;
      email: string;
      name: string;
    };
    subject: {
      id: number;
      type: string;
    };
    type: string;
  };
  author_id: number;
  author_type: string;
  created_at: string;
  id: number;
  internal: boolean;
  message: string;
  subject_id: number;
  subject_type: string;
  updated_at: string;
}

export interface DocWarnings {
  asset?: Asset;
  detect_status: string;
  form_analysis: FormAnalysis[];
  is_image_based_pdf: boolean;
  uploaded_doc_type: string;
  uploaded_doc_uuid: string;
  doc_info?: {
    rejection_reason: string;
  };
}

export type IncomeDocument = {
  type: string; // TODO Max: fix this to doc types
  warnings: DocWarnings[];
  income_sources: VerifiedIncomeSource[];
  assets:
    | {
        assets: Asset[];
        global_id: string;
      }
    | Asset[];
  files: string[];
  items: {
    asset?: {
      asset?: Asset;
      global_id: string;
    };
    book_uuid: string;
    confidence_scores: {
      key: string;
      confidence: number;
    }[];
    deductions: {
      subtotals: Deductions[];
      totals: Deductions[];
    };
    doc_analysis: {
      detect_status: string;
      is_image_based_pdf: boolean;
      uploaded_doc_type: string;
      uploaded_doc_uuid: string;
      form_analysis: FormAnalysis[];
    };
    doc_page_numbers: number[];
    doc_uuid: string;
    earnings: {
      subtotals: Deductions & { current_rate: string | null }[];
      totals: Deductions[];
    };
    employee: {
      address: {
        city: string;
      };
    };
  }[];
};

export type ReasonCode = {
  code: string;
  confidence: string;
  description: string;
};

export interface FormAnalysis {
  form_authenticity?: {
    reason_codes: ReasonCode[];
    score: number;
    version: number;
  };
  form_dashboard_url: string;
  form_type: string;
  form_uuid: string;
}

export type VerifiedIncomeReport = {
  bank_income: BankIncome[];
  net_income_summary: NetIncomeSummary[];
  employment?: PayrollEmployment[];
  employer_name?: string;
  items: PayrollItem[];
  income_count: number;
  gross_monthly_income: Money;
  net_monthly_income: Money;
  income_to_rent_ratio: number;
  income_source_type: "bank" | "payroll" | "document";
  debt_income_ratio: number;
  total_monthly_debt?: Money;
  monthly_rent_amount: Money;
  document: IncomeDocument;
  forecasted_net_monthly_income?: Money;
  notes: Note[];
  manual_review: {
    asset?: Asset["asset"];
    type: "Offer letter" | "Housing assistance program";
  }[];
  housing_assistance_program: {
    asset?: Asset["asset"];
    voucher_amount: Money;
    voucher_file: string;
  };
  debts: {
    amount: Money;
    description: string;
    name: string;
  }[];
  criteria: {
    net_income: Money;
    net_income_to_rent_ratio: number;
  };
};

export type VerificationReport<T = Record<string, unknown>> = Verification & {
  report: T;
};

type CreditReportContactAddress = {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
};

type CreditReportContactPhoneNumber = {
  type?: string | null;
  phone_number?: string | null;
  country_code?: string | null;
  extension?: string | null;
};

export type CreditReportAlertAndTriggers = {
  fraud_victim_indicator?: string | null;
  address_discrepancy_indicator?: string | null;
  alert_contacts: {
    type?:
      | string
      | {
          description: string;
        };
    date_reported?: string | null;
    date_effective?: string | null;
    status?: string | null;
    phone_numbers: CreditReportContactPhoneNumber[];
    address?: CreditReportContactAddress | null;
  }[];
};

type CreditReportConsumerStatement = {
  statement?: string | null;
  date_reported?: string | null;
  date_purged?: string | null;
};

export type CreditReportHistoricalConsumerInformation = {
  address1?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  date_first_reported?: string | null;
  date_last_reported?: string | null;
  type?: string | null;
};

enum AccountType {
  Revolving = "R",
  Installment = "I",
  Mortgage = "M",
  OpenAccount = "O" // I suppose this is OTHER
  // I'm not sure if there are other types
}

export enum SelfReportedIncomeSource {
  SelfEmployed = "Self-employed",
  Employment = "Employment",
  HousingAssistanceProgram = "Housing assistance program",
  OfferLetter = "Offer letter",
  OtherIncome = "Other income"
}

export type CreditReportAccountOverview = {
  payment_history?: CodeAndDescription[] | null;
  account_number?: string | null;
  account_name?: string | null;
  account_owner?: CodeAndDescription | null;
  portfolio_type?: {
    description: string;
    code: string | AccountType;
  } | null;
  balance?: Money | null;
  high_credit?: Money | null;
  credit_limit?: Money | null;
  scheduled_payment_amount?: Money | null;
  months_reviewed?: string | null;
  rate?: CodeAndDescription | null;
  date_opened?: string | null;
  date_reported?: string | null;
  date_closed?: string | null;
  charge_off_amount?: Money | null;
  last_activity_date?: string | null;
  narrative_codes?: CodeAndDescription[] | null;
  ballon_payment_amount?: Money | null;
  ballon_payment_date?: string | null;
  past_due_amount?: Money | null;
  actual_payment_amount?: Money | null;
  last_payment_date?: string | null;
  terms_duration?: CodeAndDescription | null;
  terms_frequency?: CodeAndDescription | null;
  deferred_payment_start?: string | null;
  previous_high_rate_one?: string | null;
  previous_high_rate_two?: string | null;
  previous_high_date_one?: string | null;
  previous_high_date_two?: string | null;
  date_major_first_delinquency_reported?: string | null;
  thirty_days_delinquency?: string | null;
  sixty_days_delinquency?: string | null;
  ninety_days_delinquency?: string | null;
  creditor_classification?: CodeAndDescription;
  original_creditor_name?: string | null;
};

export type CreditReportEmploymentInformation = {
  status?: string | null;
  occupation?: string | null;
  employer?: string | null;
  date_last_reported?: string | null;
  date_first_reported?: string | null;
};

type CreditReportCollection = {
  industry_code?: string | null;
  customer_number?: string | null;
  account_number?: string | null;
  client_name?: string | null;
  status?: CodeAndDescription | null;
  narrative_codes?: CodeAndDescription[] | null;
  date_reported?: string | null;
  original_amount?: Money | null;
  balance?: Money | null;
  creditor_classification?: CodeAndDescription;
  account_designator?: CodeAndDescription;
};

export type CreditReportConsumerInformation = {
  other_names?: string[] | null;
  death_date?: string | null;
  address1?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  address_variance_indicator?: CodeAndDescription | null;
  current_phone_number?: string | null;
  date_reported_address?: string | null;
  date_reported_phone_number?: string | null;
  ssn_status?: "Y" | "N" | "Blank" | null;
  ssn_match?: string | null;
  ssn_match_flags?: string | null;
  issue_date?: string | null;
  issue_state?: string | null;
  date_file_established?: string | null;
  date_most_recent_activity?: string | null;
};

type CreditReportBankruptcy = {
  date_filed?: string | null;
  date_reported?: string | null;
  filer?: string | null;
  type?: "B" | "I" | null;
  industry_code?: string | null;
  narrative_codes?: string | null;
  case_number?: string | null;
  customer_number?: string | null;
  disposition?: string | null;
  current_intent_or_disposition_code: CodeAndDescription | null;
  current_disposition_date: string | null;
  prior_intent_or_disposition_code: CodeAndDescription | null;
};

export type CreditReportPersonalData = {
  full_name: string;
  ssn: string;
  file_pulled?: string | null;
  customer_number?: string | null;
  dob?: string | null;
};

export type CreditRequestData = {
  city: string;
  dob: string;
  end_user_name: string;
  first_name: string;
  house_number: string;
  last_name: string;
  ssn: string;
  state: string;
  street_name: string;
  zip: string;
  model_number: string;
};

export type CreditReport = {
  hit_code?: CodeAndDescription | null;
  criteria?: {
    collections_balance: number;
    credit_score: number;
    past_due_account: number;
    unpaid_collections_balance: number;
    bankruptcy_records: number;
  };
  score?: number | null;
  personal_data: CreditReportPersonalData;
  bankruptcy_details: CreditReportBankruptcy[];
  credit_inquiries: {
    inquirer?: string | null;
    date?: string | null;
  }[];
  consumer_information: CreditReportConsumerInformation;
  collections: CreditReportCollection[];
  employment_information: CreditReportEmploymentInformation[];
  account_overview?: CreditReportAccountOverview[];
  historical_consumer_information: CreditReportHistoricalConsumerInformation[];
  consumer_statements: CreditReportConsumerStatement[];
  alerts_and_triggers: CreditReportAlertAndTriggers;
  past_due_accounts?: CreditReportAccountOverview[];
  balance_of_collections?: Money;
  balance_past_due?: Money;
  debt_balance?: Money;
  request_data: CreditRequestData;
  ssn_mismatched: boolean;
  customer_reference_number: string | null;
};
